import { useState } from "react";
import emailjs from "@emailjs/browser";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../provider/AuthProvider";

interface ReportProblemModalProps {
  onClose: () => void;
}

const ReportProblemModal = ({ onClose }: ReportProblemModalProps) => {
  const [reportMessage, setReportMessage] = useState("");
  const [reportTitle, setReportTitle] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const { userName } = useAuth();
  const location = useLocation();

  const handleSubmitReport = () => {
    if (!reportTitle.trim() || !reportMessage.trim()) return;

    const templateParams = {
      name: userName || "Анонимен потребител",
      title: reportTitle,
      message: reportMessage,
      time: moment().format("YYYY-MM-DD HH:mm:ss"),
      currentScreen: location.pathname,
    };

    setIsLoading(true);
    setProgress(0);

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 25;
      });
    }, 300);

    emailjs
      .send(
        // @ts-ignore
        import.meta.env.VITE_EMAIL_SERVICE_ID,
        // @ts-ignore
        import.meta.env.VITE_EMAIL_TEMPLATE_ID,
        templateParams,
        // @ts-ignore
        import.meta.env.VITE_EMAIL_PUBLIC_KEY
      )
      .then(() => {
        setIsSubmitted(true);
        setTimeout(() => {
          onClose();
          setReportMessage("");
          setReportTitle("");
          setIsSubmitted(false);
          setIsLoading(false);
          setProgress(100);
        }, 2000);
      })
      .catch((error) => {
        setIsLoading(false);
        setProgress(0);
        throw new Error("Failed to send email:", error);
      });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-gray-800">
        <h2 className="mb-4 text-xl font-semibold text-gray-800 dark:text-white">
          Съобщаване за проблем
        </h2>
        <input
          type="text"
          className="mb-3 w-full rounded-md border border-zinc-300 p-2 text-black dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          placeholder="Въведете кратко заглавие..."
          value={reportTitle}
          onChange={(e) => setReportTitle(e.target.value)}
        />
        <textarea
          className="w-full rounded-md border border-zinc-300 p-2 text-black dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          rows={5}
          placeholder="Опишете проблема..."
          value={reportMessage}
          onChange={(e) => setReportMessage(e.target.value)}
        ></textarea>

        {/* Linear Progress Bar */}
        {isLoading && (
          <div className="mt-4 h-2 w-full rounded-full bg-gray-200">
            <div
              className={`h-2 rounded-full transition-all duration-300 ease-in-out ${
                isLoading ? "bg-blue-600" : "bg-green-500"
              }`}
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="bg-gray rounded px-4 py-2 text-sm font-medium hover:bg-gray-400 dark:bg-gray-600 dark:text-white dark:hover:bg-gray-700"
          >
            Затвори
          </button>
          <button
            onClick={handleSubmitReport}
            className={`rounded px-4 py-2 text-sm font-medium ${
              isSubmitted
                ? "bg-green-600 hover:bg-green-700"
                : isLoading
                ? "bg-blue-600 hover:bg-blue-700"
                : "bg-blue-600 hover:bg-blue-700"
            } text-white disabled:opacity-50`}
            disabled={isLoading}
          >
            {isSubmitted
              ? "Изпратено!"
              : isLoading
              ? "Изпраща се..."
              : "Изпрати"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportProblemModal;
